import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO, { breadcrumbJsonLd, faqJsonLd } from '../../components/SEO';
import ExceptionStory from '../home/components/ExceptionStory';
import type { FeatureData } from './FeaturePage';
import AgentsExplorer from './AgentsExplorer';

const DEMO_URL = 'https://meetings.hubspot.com/aditya145';

const IMG = {
  console: '/images/features/ai-agents/agents-console.jpg',
  settings: '/images/features/ai-agents/agent-settings.jpg',
  audit: '/images/features/ai-agents/audit-trail.jpg',
} as const;

const AUTONOMY = [
  {
    icon: 'ri-lightbulb-flash-line',
    mode: 'Suggest',
    title: 'The agent recommends, a person decides',
    body: 'Best for new playbooks. Agents surface the risk and the fix; dispatchers act on it.',
  },
  {
    icon: 'ri-hand-coin-line',
    mode: 'Approve',
    title: 'The agent prepares, you confirm in one tap',
    body: 'The action is ready to go, like “Reassign 2 stops”. Nothing changes until someone approves.',
  },
  {
    icon: 'ri-robot-2-line',
    mode: 'Automate',
    title: 'The agent acts within your guardrails',
    body: 'Routine moves run on their own inside the limits you set, and anything outside them comes back to a person.',
  },
];

const GUARDRAILS = [
  { icon: 'ri-shield-check-line', title: 'Policy guardrails', text: 'SLA windows, cost ceilings, carrier tiers and zones limit what an agent can do on its own.' },
  { icon: 'ri-user-settings-line', title: 'Role-based permissions', text: 'Agents and Copilot can only do what the signed-in user’s role allows.' },
  { icon: 'ri-history-line', title: 'Full audit trail', text: 'Every suggestion, approval, override and action is logged against the order or trip.' },
  { icon: 'ri-arrow-go-back-line', title: 'Always overridable', text: 'Any agent decision can be changed. Overrides are recorded and shape future suggestions.' },
];

const SIGNALS = [
  { icon: 'ri-file-list-3-line', title: 'Orders', body: 'Items, time windows, priorities and service levels from your OMS, store or API.' },
  { icon: 'ri-map-pin-time-line', title: 'Live GPS', body: 'Driver position, speed, dwell time and route adherence.' },
  { icon: 'ri-truck-line', title: 'Capacity', body: 'Driver shifts, vehicle limits and carrier availability.' },
  { icon: 'ri-traffic-light-line', title: 'Traffic', body: 'Live traffic and road conditions that change ETAs.' },
  { icon: 'ri-building-2-line', title: 'Hubs', body: 'Inbound waves, sort windows and staffing.' },
  { icon: 'ri-bank-card-line', title: 'Delivery rules', body: 'COD, proof-of-delivery and failed-attempt policies.' },
];

const PLAN_ROWS: { label: string; premium: boolean; enterprise: boolean }[] = [
  { label: 'AI auto-dispatch and auto-allocation', premium: true, enterprise: true },
  { label: 'Route recovery and exception containment', premium: false, enterprise: true },
  { label: 'SLA guardian and customer comms agents', premium: false, enterprise: true },
  { label: 'Carrier matching and hub load-balancing', premium: false, enterprise: true },
  { label: 'Driver coach agent', premium: false, enterprise: true },
  { label: 'Playbooks and guardrails per hub or lane', premium: false, enterprise: true },
];

export default function AIAgentsFeaturePage({ data }: { data: FeatureData }) {
  const { t } = useTranslation();

  return (
    <div className="bg-white pt-24 text-gray-900">
      <SEO
        title="AI Agents for Delivery Dispatch and Exceptions"
        description={data.description}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: 'Features', url: 'https://geofleet.ai/features' },
            { name: data.name, url: `https://geofleet.ai/features/${data.slug}` },
          ]),
          faqJsonLd(data.faq),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white pb-16 pt-10 lg:pb-20 lg:pt-14">
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[min(560px,90vw)] translate-x-1/4 -translate-y-1/3 rounded-full bg-blue-500/[0.08] blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16">
          <div>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm">
              <i className="ri-robot-2-line" aria-hidden />
              {data.tagline}
            </div>
            <h1 className="text-balance text-4xl font-bold tracking-tight text-gray-900 lg:text-5xl lg:leading-[1.08]">
              {data.headline} <span className="text-blue-600">{data.headlineHighlight}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">{data.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/30 hover:bg-blue-700"
              >
                <i className="ri-calendar-check-line" aria-hidden />
                {t('common.ctaBookDemo')}
              </a>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-6 py-3.5 text-sm font-semibold text-gray-800 hover:border-blue-300"
              >
                See plans
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
              </Link>
            </div>
            <dl className="mt-10 grid max-w-lg grid-cols-3 gap-6">
              {data.heroStats.map((s) => (
                <div key={s.label} className="flex flex-col-reverse border-s-2 border-blue-600 ps-4">
                  <dt className="text-xs text-gray-500">{s.label}</dt>
                  <dd className="text-xl font-bold tracking-tight text-gray-900">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-2xl shadow-blue-900/10">
            <img
              src={IMG.console}
              alt="Geofleet AI agents console showing each agent’s autonomy level, a live activity feed and today’s impact"
              width={1586}
              height={992}
              className="h-auto w-full rounded-xl"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* One exception, end to end — same story as the homepage */}
      <ExceptionStory showPillars={false} />

      {/* Meet the agents */}
      <AgentsExplorer />

      {/* Control */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-20 lg:py-28" aria-labelledby="agents-control">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">You stay in control</p>
            <h2 id="agents-control" className="mt-2 scroll-mt-32 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
              Choose how much each agent does on its own
            </h2>
            <p className="mt-4 leading-relaxed text-gray-600">
              Set the level per agent, hub or type of action. Most teams start at Suggest and move routine work to Automate as trust builds.
            </p>
          </div>

          <ol className="m-0 grid list-none gap-4 p-0 md:grid-cols-3">
            {AUTONOMY.map((level, i) => (
              <li key={level.mode} className="relative rounded-2xl border border-gray-200 bg-white p-7">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                    <i className={`${level.icon} text-xl`} aria-hidden />
                  </span>
                  <span className="text-sm font-bold uppercase tracking-wider text-blue-700">
                    {i + 1}. {level.mode}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{level.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{level.body}</p>
                {i < AUTONOMY.length - 1 && (
                  <i
                    className="ri-arrow-right-line absolute -end-4 top-1/2 z-10 hidden -translate-y-1/2 rounded-full border border-gray-200 bg-white p-1.5 text-gray-400 md:block rtl:rotate-180"
                    aria-hidden
                  />
                )}
              </li>
            ))}
          </ol>

          <figure className="mx-auto mt-10 max-w-5xl">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl shadow-blue-900/10">
              <img
                src={IMG.settings}
                alt="Agent settings for Exception containment: autonomy set to Approve, with guardrails for extra distance, cost, priority orders and hubs"
                width={1584}
                height={993}
                loading="lazy"
                className="h-auto w-full rounded-xl"
              />
            </div>
            <figcaption className="mt-3 text-center text-sm text-gray-500">
              Each agent has its own autonomy level, guardrails and approvers.
            </figcaption>
          </figure>

          <div className="mt-20 grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <h3 className="text-2xl font-bold tracking-tight text-gray-900">Every decision, explained</h3>
              <p className="mt-3 leading-relaxed text-gray-600">
                The audit trail shows what each agent saw, why it chose an action and who approved it, so you can review any change later.
              </p>
              <ul className="m-0 mt-8 list-none space-y-5 p-0">
                {GUARDRAILS.map((g) => (
                  <li key={g.title} className="flex gap-3">
                    <i className={`${g.icon} mt-0.5 text-xl text-emerald-600`} aria-hidden />
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">{g.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-gray-600">{g.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl shadow-blue-900/10">
                <img
                  src={IMG.audit}
                  alt="Audit trail of AI agent actions, with one reassignment expanded to show what the agent saw and why it chose the action"
                  width={1584}
                  height={993}
                  loading="lazy"
                  className="h-auto w-full rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What agents see */}
      <section className="bg-white py-20 lg:py-24" aria-labelledby="agents-signals">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">Live context</p>
            <h2 id="agents-signals" className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              Decisions based on what is happening right now
            </h2>
            <p className="mt-4 leading-relaxed text-gray-600">
              A general-purpose chatbot can’t see your fleet. Geofleet agents read the same live data as your command centre, so every
              suggestion fits the road as it is.
            </p>
            <Link to="/integrations" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-600">
              See integrations
              <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
            </Link>
          </div>
          <ul className="m-0 grid list-none gap-4 p-0 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3">
            {SIGNALS.map((s) => (
              <li key={s.title} className="rounded-2xl border border-gray-200 bg-slate-50/60 p-5">
                <i className={`${s.icon} text-xl text-blue-600`} aria-hidden />
                <h3 className="mt-3 text-sm font-bold text-gray-900">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">{s.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Plans */}
      <section className="border-t border-gray-100 bg-slate-50/70 py-20 lg:py-24" aria-labelledby="agents-plans">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">Plans</p>
            <h2 id="agents-plans" className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              Which plan includes which agents
            </h2>
          </div>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th scope="col" className="px-3 py-4 font-semibold text-gray-900 sm:px-5">
                    Capability
                  </th>
                  <th scope="col" className="w-24 px-2 py-4 text-center font-semibold text-gray-900 sm:w-32 sm:px-5">
                    Premium
                    <span className="block text-xs font-normal text-gray-500">$29 /driver/mo</span>
                  </th>
                  <th scope="col" className="w-24 px-2 py-4 text-center font-semibold text-gray-900 sm:w-32 sm:px-5">
                    Enterprise
                    <span className="block text-xs font-normal text-gray-500">Custom</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PLAN_ROWS.map((row) => (
                  <tr key={row.label} className="border-b border-gray-100 last:border-0">
                    <th scope="row" className="px-3 py-3.5 text-left font-normal text-gray-700 sm:px-5">
                      {row.label}
                    </th>
                    {[row.premium, row.enterprise].map((on, i) => (
                      <td key={i} className="px-2 py-3.5 text-center sm:px-5">
                        {on ? (
                          <i className="ri-check-line text-lg text-emerald-600" aria-label="Included" />
                        ) : (
                          <span className="text-gray-300" aria-label="Not included">
                            —
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-center text-sm text-gray-500">
            Auto-dispatch starts on Premium; the full agent suite is on Enterprise.{' '}
            <Link to="/pricing" className="font-semibold text-blue-700 hover:text-blue-600">
              Compare all plans
            </Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20" aria-labelledby="agents-faq">
        <div className="mx-auto max-w-3xl px-6">
          <h2 id="agents-faq" className="mb-10 text-center text-3xl font-bold tracking-tight text-gray-900">
            {t('common.faqTitle')}
          </h2>
          <div className="space-y-3">
            {data.faq.map((item, i) => (
              <details key={item.question} open={i === 0} className="group rounded-2xl border border-gray-200 bg-white px-5 py-4 open:shadow-md">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
                  <h3 className="text-base">{item.question}</h3>
                  <i className="ri-add-line shrink-0 text-xl text-gray-400 transition-transform group-open:rotate-45" aria-hidden />
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gray-950 py-16 lg:py-20">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-950/80 via-gray-950 to-indigo-950/40" />
        <div className="relative mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-2xl font-bold text-white lg:text-3xl">Put agents to work on your routes</h2>
          <p className="mt-4 leading-relaxed text-gray-300">
            We’ll map the agents to your hubs, drivers and SLAs, and show you what they would have done on a day of your own orders.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/35 hover:bg-blue-500"
            >
              <i className="ri-calendar-check-line" aria-hidden />
              {t('common.ctaBookDemo')}
            </a>
            <Link
              to="/pricing"
              className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl border-2 border-white/30 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10"
            >
              See plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
